import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetReceivablesAgingUseCase } from "../../domain/use-cases/GetReceivablesAgingUseCase";
import { formatPlAmount } from "../utils/formatPlAmount";
import type { ReceivablesAgingEntity } from "../../domain/entities/ReceivablesAgingEntity";
import {
  getRevenueBootstrapData,
  getRevenueBootstrapPromise,
} from "../state/revenueBootstrapStore";

export interface AgingBucketData {
  color: string;
  label: string;
  amount: string;
  percent: string;
  barHeight?: number;
}

export interface ReceivablesAgingData {
  title: string;
  /** e.g. "Outstanding as of Mar 12, 2025"; empty if API omits asOfDate. */
  asOfSubtitle: string;
  totalOutstanding: string;
  overdueAmount: string;
  notOverdueAmount: string;
  buckets: AgingBucketData[];
  alertMessage: string;
}

const BUCKET_COLORS = ["#22c55e", "#3b82f6", "#d97706", "#ef4444"];
/** Stem height cap (px); keep in sync with chart row min-height in ReceivablesAgingSection. */
const MAX_BAR_HEIGHT = 260;
const MIN_BAR_HEIGHT = 8;

/** Native `title` / aria text for receivables scope (matches product copy). */
export const RECEIVABLES_AGING_SCOPE_TOOLTIP =
  "Shows open balances split by due date, and buckets built from invoices with due_date on/before this date.";

function formatAsOfSubtitle(isoYmd: string): string {
  const parts = isoYmd.split("-").map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return "";
  }
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return "";
  }
  const label = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(dt);
  return `Outstanding as of ${label}`;
}

const repository = new ClientOverviewRepositoryImpl();
const getReceivablesAgingUseCase = new GetReceivablesAgingUseCase(repository);

function entityToDisplayData(
  entity: ReceivablesAgingEntity,
): ReceivablesAgingData {
  const buckets = entity.buckets;
  const total = entity.totalOutstandingAmount;
  const overdue = entity.overdueAmount;
  const notOverdue = entity.notOverdueAmount;
  const maxAmount = buckets.length
    ? Math.max(...buckets.map((b) => b.amount), 0)
    : 0;

  const displayBuckets: AgingBucketData[] = buckets.map((b, i) => {
    const color = BUCKET_COLORS[i] ?? BUCKET_COLORS[0];
    let barHeight = 0;
    if (maxAmount > 0 && b.amount > 0) {
      barHeight = Math.round((b.amount / maxAmount) * MAX_BAR_HEIGHT);
      if (barHeight < MIN_BAR_HEIGHT) barHeight = MIN_BAR_HEIGHT;
    }
    return {
      color,
      label: b.label,
      amount: formatPlAmount(b.amount),
      percent: `${b.percent}%`,
      barHeight,
    };
  });

  return {
    title: "Receivables Aging",
    asOfSubtitle: formatAsOfSubtitle(entity.asOfDate),
    totalOutstanding:
      total > 0
        ? `${formatPlAmount(total)} outstanding`
        : `${formatPlAmount(0)} outstanding`,
    overdueAmount:
      overdue > 0 ? `${formatPlAmount(overdue)} overdue` : `${formatPlAmount(0)} overdue`,
    notOverdueAmount:
      notOverdue > 0
        ? `${formatPlAmount(notOverdue)} not overdue`
        : `${formatPlAmount(0)} not overdue`,
    buckets: displayBuckets,
    alertMessage: entity.alertMessage ?? "",
  };
}

export function useReceivablesAging(): ReceivablesAgingData & {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [entity, setEntity] = useState<ReceivablesAgingEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let bootstrapEntity = getRevenueBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getRevenueBootstrapPromise();
        if (bootstrapPromise) {
          try {
            bootstrapEntity = await bootstrapPromise;
          } catch {
            bootstrapEntity = null;
          }
        }
      }

      if (bootstrapEntity) {
        const section = bootstrapEntity.sections.receivablesAging;
        if (section.status === "ok" && section.data) {
          setEntity(section.data);
          return;
        }
      }

      const data = await getReceivablesAgingUseCase.execute();
      setEntity(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load receivables aging",
      );
      setEntity(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const defaultData: ReceivablesAgingData = {
    title: "Receivables Aging",
    asOfSubtitle: "",
    totalOutstanding: "—",
    overdueAmount: "—",
    notOverdueAmount: "—",
    buckets: [],
    alertMessage: "",
  };

  const displayData = entity ? entityToDisplayData(entity) : defaultData;

  return {
    ...displayData,
    loading,
    error,
    refetch: load,
  };
}
